import type { AccountStatus } from '../api/accounts';
import type { AccountMoment } from './accountOffers';

/**
 * How the rest of the game talks to the account layer
 * (components/AccountLayer.tsx) without threading props through the app:
 * report a good moment, open the account board (Settings), and read the
 * account's status (Settings' row).
 */

type MomentListener = (moment: AccountMoment) => void;
const momentListeners = new Set<MomentListener>();

/** A good moment just happened (the road reports a chapter done). */
export function noteAccountMoment(moment: AccountMoment): void {
  for (const l of momentListeners) l(moment);
}

export function onAccountMoment(listener: MomentListener): () => void {
  momentListeners.add(listener);
  return () => momentListeners.delete(listener);
}

const openListeners = new Set<() => void>();

/** Open the account board: the account, or a board to create one or sign in. */
export function openAccountBoard(): void {
  for (const l of openListeners) l();
}

export function onOpenAccountBoard(listener: () => void): () => void {
  openListeners.add(listener);
  return () => openListeners.delete(listener);
}

let status: AccountStatus | null = null;
const statusListeners = new Set<() => void>();

/** The account layer publishes what it knows; null when accounts are off. */
export function publishAccountStatus(next: AccountStatus | null): void {
  if (next === status) return;
  status = next;
  for (const l of statusListeners) l();
}

export function accountStatusSnapshot(): AccountStatus | null {
  return status;
}

export function subscribeAccountStatus(listener: () => void): () => void {
  statusListeners.add(listener);
  return () => statusListeners.delete(listener);
}
